import { useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import {
  type ApiPartnerRegistration,
  type ApiRegistration,
  type ApiOrganization as PartnerRegisterOrganization,
  type ApiStakeholder as PartnerRegisterStakeholder,
  registerPartnersQueries,
} from '@repo/api-kit/queries/register-partners';
import {
  type ApiOrganization as RegisterOrganization,
  registerOrganizationQueries,
} from '@repo/api-kit/queries/register-organization';
import {
  membershipQueries,
  type ApiStakeholder as RegisterStakeholder,
} from '@repo/api-kit/queries/register-memberships';
import { isResponseError } from '@repo/api-kit/queries/utils';
import { shouldSendToSentry } from '@repo/runtime-kit/utils/should-send-to-sentry';
import { useSentry } from '@repo/runtime-kit/sentry/context';

interface PrimaryRegistrationData {
  registration?: ApiRegistration;
  organization: PartnerRegisterOrganization;
  owner?: PartnerRegisterStakeholder;
}

interface FallbackRegistrationData {
  registration?: ApiPartnerRegistration;
  organization: RegisterOrganization;
  owner?: RegisterStakeholder;
}

export class RegistrationOrganizationMissingError extends Error {
  constructor(registrationId: string) {
    super(`partner registration ${registrationId} has no organization yet`);
    this.name = 'RegistrationOrganizationMissingError';
  }
}

export type RegistrationWithRelations = PrimaryRegistrationData | FallbackRegistrationData;

function useRegistrationQueryOptions(registrationId: string | undefined) {
  const queryClient = useQueryClient();
  const sentry = useSentry();

  return {
    queryKey: [
      ...registerPartnersQueries.all,
      'registration-with-relations',
      registrationId,
    ] as const,
    queryFn: async (): Promise<RegistrationWithRelations> => {
      if (!registrationId) {
        throw new Error('Registration ID is required');
      }

      try {
        const result = await queryClient.ensureQueryData(
          registerPartnersQueries.registration(registrationId)
        );
        const owner = result.stakeholders.find(s => s.owner === true);

        return {
          registration: result.registration,
          organization: result.organization,
          owner,
        };
      } catch (error: unknown) {
        if (isResponseError(error) && error.status === 404) {
          return fetchFallbackRegistration(queryClient, sentry, registrationId);
        }

        if (shouldSendToSentry(error)) {
          sentry.captureException(error);
        }

        throw error;
      }
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  };
}

async function fetchFallbackRegistration(
  queryClient: ReturnType<typeof useQueryClient>,
  sentry: ReturnType<typeof useSentry>,
  registrationId: string
): Promise<FallbackRegistrationData> {
  try {
    const data = await queryClient.ensureQueryData(
      registerPartnersQueries.partnerRegistration(registrationId)
    );

    const organizationId = data.partnerRegistration?.organizationId;
    // An empty id would hit the organizations list endpoint, whose response has no `organization` key.
    if (!organizationId) {
      throw new RegistrationOrganizationMissingError(registrationId);
    }

    const organization = await queryClient.ensureQueryData(
      registerOrganizationQueries.item(organizationId)
    );

    const owner = await queryClient.ensureQueryData(membershipQueries.item(organization.ownerId));

    return {
      registration: data.partnerRegistration,
      owner,
      organization,
    };
  } catch (fallbackError: unknown) {
    if (
      !(fallbackError instanceof RegistrationOrganizationMissingError) &&
      shouldSendToSentry(fallbackError)
    ) {
      sentry.captureException(fallbackError);
    }
    throw fallbackError;
  }
}

export function useRegistration(registrationId?: string) {
  const options = useRegistrationQueryOptions(registrationId);
  return useQuery({
    ...options,
    enabled: Boolean(registrationId),
  });
}

export function useSuspenseRegistration(registrationId?: string) {
  if (!registrationId) {
    throw new Error('Registration ID is required for suspense query');
  }

  return useSuspenseQuery(useRegistrationQueryOptions(registrationId));
}
